import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Megaphone, Plus, ArrowRight, Users, Gift, Sparkles, MoonStar, Check, ChevronDown } from "lucide-react";

const SEGMENTS = [
  { id: "all",              label: "All active holders",   Icon: Users,     desc: "Everyone with a live pass right now." },
  { id: "reward_available", label: "Reward ready",         Icon: Gift,      desc: "Passes sitting on an unlocked reward. Nudge them to claim it." },
  { id: "new_30_days",      label: "New in last 30 days",  Icon: Sparkles,  desc: "Fresh joiners. Welcome them back for a second visit." },
  { id: "dormant_4_weeks",  label: "Dormant 4 weeks+",     Icon: MoonStar,  desc: "Haven't scanned in a month. Win them back with a reason." },
];

// Ready-made message starters merchants can drop straight into the textarea.
// Keeps the composer from feeling like a blank page.
const STARTERS = [
  "Every coffee earns double stamps today only. See you before 3pm.",
  "Your reward is unlocked. Pop in this week to claim it.",
  "We miss you. The next stamp is on us until Sunday.",
];

const MSG_MAX = 90;

export default function Campaigns() {
  const { activeOrg } = useAuth();
  const [rows, setRows] = useState([]);
  const [progs, setProgs] = useState([]);
  const [audience, setAudience] = useState({ all: 0, reward_available: 0, new_30_days: 0, dormant_4_weeks: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", message: "", program_id: "", audience: "all" });
  const [publishing, setPublishing] = useState(false);
  const previewDebounce = useRef();

  const load = () => activeOrg && api.get(`/orgs/${activeOrg.id}/campaigns`).then((r) => setRows(r.data));

  useEffect(() => {
    if (!activeOrg) return;
    load();
    api.get(`/orgs/${activeOrg.id}/programs`).then((r) => setProgs(r.data));
  }, [activeOrg]);

  // Refresh audience counts whenever the selected program changes.
  useEffect(() => {
    if (!activeOrg || !open) return;
    clearTimeout(previewDebounce.current);
    previewDebounce.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (form.program_id) params.set("program_id", form.program_id);
      api.get(`/orgs/${activeOrg.id}/campaigns/audience-preview?${params.toString()}`)
        .then((r) => setAudience(r.data.counts))
        .catch(() => {});
    }, 250);
  }, [activeOrg, open, form.program_id]);

  const activeSeg = SEGMENTS.find((s) => s.id === form.audience) || SEGMENTS[0];
  const canPublish = form.name.trim() && form.message.trim() && (audience[form.audience] ?? 0) > 0 && !publishing;
  const audienceEmpty = (audience[form.audience] ?? 0) === 0;

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/orgs/${activeOrg.id}/campaigns`, form);
      toast.success(`Campaign live. ${audience[form.audience]} pass holders notified.`);
      setOpen(false);
      setForm({ name: "", message: "", program_id: "", audience: "all" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPublishing(false); }
  };

  const reset = () => { setOpen(false); setForm({ name: "", message: "", program_id: "", audience: "all" }); };

  // Esc key closes the composer when open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") reset(); };
    window.addEventListener("keydown", onKey);
    // scroll to top when composer opens so the form is fully in view
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!activeOrg) return null;

  return (
    <div>
      <PageHeader
        title="Campaigns"
        subtitle="Send a message straight to your members' wallet passes."
        action={!open && (
          <Button data-testid="new-campaign" onClick={() => setOpen(true)} className="bg-[#C8FF4D] text-[#0B0D10] hover:bg-[#b8ef3d] rounded-full font-semibold">
            <Plus size={16} className="mr-1" />New campaign
          </Button>
        )}
      />

      {open && (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8 items-start">
          {/* --- LEFT: composer form --- */}
          <div className="rounded-3xl bg-white border border-[#E8E5DE] p-7 space-y-8" data-testid="campaign-composer">
            {/* name */}
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Internal name</Label>
                <span className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/45">customers won't see this</span>
              </div>
              <Input data-testid="campaign-name" value={form.name} placeholder="Double Stamp Tuesday" onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white" />
            </div>

            {/* segment picker */}
            <div className="space-y-3">
              <Label>Who receives it</Label>
              <div className="rounded-2xl border border-[#E8E5DE] divide-y divide-[#E8E5DE] overflow-hidden" data-testid="segment-grid">
                {SEGMENTS.map(({ id, label, Icon, desc }) => {
                  const on = form.audience === id;
                  const count = audience[id] ?? 0;
                  return (
                    <button key={id} type="button" data-testid={`segment-${id}`} onClick={() => setForm({ ...form, audience: id })}
                      className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-colors ${on ? "bg-[#0B0D10] text-white" : "bg-white hover:bg-[#F8F6F0]"}`}>
                      <span className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${on ? "bg-[#C8FF4D] text-[#0B0D10]" : "bg-[#0B0D10]/[0.05] text-[#0B0D10]/70"}`}><Icon size={16} strokeWidth={2} /></span>
                      <span className="flex-1 min-w-0">
                        <span className={`block font-semibold text-[14px] ${on ? "text-white" : "text-[#0B0D10]"}`}>{label}</span>
                        <span className={`block text-[12.5px] mt-0.5 leading-snug ${on ? "text-white/60" : "text-[#3a3d44]/60"}`}>{desc}</span>
                      </span>
                      <span className="text-right shrink-0">
                        <span className={`block font-display font-extrabold text-[20px] leading-none tabular-nums ${on ? "text-[#C8FF4D]" : "text-[#0B0D10]"}`}>{count}</span>
                        <span className={`block text-[10px] uppercase tracking-[0.12em] mt-1 ${on ? "text-white/55" : "text-[#3a3d44]/45"}`}>holders</span>
                      </span>
                      {on && <Check size={16} strokeWidth={3} className="text-[#C8FF4D] shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* message */}
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <Label>Message</Label>
                <span className={`text-[11px] tabular-nums ${form.message.length > MSG_MAX ? "text-red-500" : "text-[#3a3d44]/45"}`}>{form.message.length}/{MSG_MAX}</span>
              </div>
              <Textarea data-testid="campaign-message" value={form.message} maxLength={MSG_MAX + 20} rows={3} placeholder="Every coffee earns two stamps today only. See you before 3pm." onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white resize-none" />
              <p className="text-[12.5px] text-[#3a3d44]/60">Shows on the lock screen exactly as you type. Keep it short. One sentence lands hardest.</p>
              {/* Starter chips — merchants who freeze at a blank textarea can tap a
                  proven message and edit from there. Faster to launch than staring. */}
              {!form.message && (
                <div className="pt-3 flex flex-wrap gap-2" data-testid="campaign-starters">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/40 self-center">Try one</span>
                  {STARTERS.map((s) => (
                    <button key={s} type="button" data-testid="campaign-starter" onClick={() => setForm({ ...form, message: s })}
                      className="btn-lift text-[12px] text-[#0B0D10]/75 bg-[#F6F3EC] hover:bg-[#0B0D10] hover:text-white border border-[#E4E0D6] hover:border-[#0B0D10] rounded-full px-3 py-1.5 transition-all">
                      {s.length > 42 ? s.slice(0, 42) + "…" : s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2 max-w-sm">
              <Label>Link to a program <span className="text-[#3a3d44]/45 font-normal">(optional)</span></Label>
              <select data-testid="campaign-program" value={form.program_id} onChange={(e) => setForm({ ...form, program_id: e.target.value })} className="w-full h-10 rounded-md border border-[#E8E5DE] bg-white px-3 text-sm">
                <option value="">Every program</option>
                {progs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <p className="text-[12.5px] text-[#3a3d44]/60">Only pass holders on that program receive it.</p>
            </div>

            {/* publish bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-[#E8E5DE] pt-6">
              <div className="text-[13.5px] text-[#3a3d44]">
                {audienceEmpty ? (
                  <span className="text-[#3a3d44]/60">This segment is empty. <span className="text-[#0B0D10] font-medium">No one would receive it.</span> Pick another segment.</span>
                ) : (
                  <>Landing on <span className="font-semibold text-[#0B0D10] tabular-nums">{audience[form.audience] ?? 0}</span> {audience[form.audience] === 1 ? "phone" : "phones"}
                  <span className="text-[#3a3d44]/45"> · {activeSeg.label.toLowerCase()}</span></>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} className="rounded-full">Cancel</Button>
                <Button data-testid="send-campaign" onClick={publish} disabled={!canPublish} className="bg-[#0B0D10] text-white hover:bg-black rounded-full font-semibold">
                  {publishing ? "Publishing…" : <>Publish now <ArrowRight size={15} className="ml-1.5" /></>}
                </Button>
              </div>
            </div>
          </div>

          {/* --- RIGHT: lock-screen preview --- */}
          <div className="lg:sticky lg:top-24" data-testid="campaign-preview">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/50 mb-3 font-semibold">Lock-screen preview</div>
            <div className="rounded-3xl bg-[#0B0D10] p-5 sm:p-6">
              <div className="text-center text-white/70 text-[13px] mb-1">{new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}</div>
              <div className="text-center text-white font-display font-extrabold text-[56px] leading-none tracking-[-0.03em] mb-8">9:41</div>
              <div className="rounded-2xl bg-white/[0.14] backdrop-blur-md p-3.5 flex gap-3 items-start" data-testid="campaign-preview-notification">
                <span className="h-9 w-9 rounded-[10px] grid place-items-center shrink-0 font-display font-extrabold text-[13px]" style={{ background: activeOrg.brand?.secondary_color || "#C8FF4D", color: "#0B0D10" }}>{(activeOrg.name || "B").slice(0, 1).toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2"><span className="text-white font-semibold text-[13.5px] truncate">{activeOrg.name}</span><span className="text-white/50 text-[11px] shrink-0">now</span></span>
                  <span className={`block text-[13px] leading-snug mt-0.5 break-words ${form.message ? "text-white/90" : "text-white/45 italic"}`} data-testid="campaign-preview-message">{form.message || "Your message appears here"}</span>
                </span>
              </div>
            </div>
            <p className="text-[12px] text-[#3a3d44]/55 mt-4 leading-relaxed">Delivered to every pass in the segment as a wallet notification. No app, no email list.</p>
          </div>
        </div>
      )}

      {/* --- past campaigns --- */}
      {!open && (
        rows.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E8E5DE] p-16 text-center">
            <Megaphone size={28} className="mx-auto text-[#25282E]/40 mb-3" />
            <h3 className="font-display text-xl font-semibold">Give them a reason to return this week.</h3>
            <p className="text-[#3a3d44]/60 mt-2 text-[14px]">Every campaign lands straight on the lock screen. No ads. No email list.</p>
            <Button onClick={() => setOpen(true)} className="mt-6 bg-[#0B0D10] text-white rounded-full font-semibold" data-testid="empty-new-campaign">
              <Plus size={16} className="mr-1" /> Create your first campaign
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#3a3d44]/50 mb-1">Recent campaigns · <span className="text-[#3a3d44]/40">attribution within 7 days of send</span></div>
            {rows.map((c) => (
              <CampaignRow key={c.id} c={c} orgId={activeOrg.id} SEGMENTS={SEGMENTS} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

/* ---------- Rich list row with inline analytics + click-to-open chart ---------- */
function CampaignRow({ c, orgId, SEGMENTS }) {
  const [openDetail, setOpenDetail] = useState(false);
  const [detail, setDetail] = useState(null);
  const [delivery, setDelivery] = useState(c.delivery || null);
  const seg = SEGMENTS.find((s) => s.id === c.audience) || SEGMENTS[0];
  const a = c.analytics || { delivered: c.audience_size || 0, attributed_visits: 0, attributed_redemptions: 0, attributed_holders: 0, attribution_rate: 0, window_days: 7 };
  const ageHours = c.created_at ? Math.max(0, (Date.now() - Date.parse(c.created_at)) / 3600000) : 0;
  const ageLabel = ageHours < 1 ? "just now" : ageHours < 24 ? `${Math.round(ageHours)}h ago` : `${Math.round(ageHours / 24)}d ago`;
  const stillMeasuring = ageHours < 24 * 7;
  const d = delivery;
  const sending = d && d.status !== "done";

  // Live delivery: poll fast while sending, then for a few minutes while phones confirm.
  useEffect(() => {
    const settling = d && ageHours < 0.25 && d.confirmed_holders < d.pushed_holders;
    if (!sending && !settling) return undefined;
    const t = setInterval(() => {
      api.get(`/orgs/${orgId}/campaigns/${c.id}`).then((r) => { setDelivery(r.data.delivery); setDetail((prev) => (prev ? r.data : prev)); }).catch(() => {});
    }, sending ? 3000 : 10000);
    return () => clearInterval(t);
  }, [sending, d?.confirmed_holders, d?.pushed_holders, c.id, orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async () => {
    const next = !openDetail;
    setOpenDetail(next);
    if (next && !detail) {
      try { const { data } = await api.get(`/orgs/${orgId}/campaigns/${c.id}`); setDetail(data); setDelivery(data.delivery || null); } catch (e) {}
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#E8E5DE]" data-testid={`campaign-${c.id}`}>
      <button type="button" onClick={toggle} data-testid={`campaign-row-${c.id}`}
        className="w-full text-left p-5 flex items-start gap-4 hover:bg-[#0B0D10]/[0.015] transition-colors rounded-2xl">
        {/* segment icon */}
        <span className="h-11 w-11 rounded-[12px] bg-[#0B0D10]/[0.05] grid place-items-center shrink-0"><seg.Icon size={19} className="text-[#0B0D10]/70" strokeWidth={2} /></span>

        {/* main content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-[16px] truncate">{c.name}</h3>
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#3a3d44]/50 shrink-0">{seg.label}</span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#3a3d44]/35 shrink-0">· {ageLabel}</span>
            <DeliveryPill d={d} id={c.id} />
          </div>
          <p className="text-[13.5px] text-[#3a3d44] mt-1 truncate">{c.message}</p>

          {/* inline analytics strip */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {d ? <Stat value={`${d.pushed_holders}/${d.installed}`} label="phones pushed" tone="ink" testid={`campaign-pushed-${c.id}`} />
               : <Stat value={a.delivered} label="delivered" tone="ink" />}
            {d ? <Stat value={d.confirmed_holders} label="updated on phone" tone="lime" testid={`campaign-confirmed-${c.id}`} />
               : <Stat value={a.attributed_holders} label="came back" tone="lime" />}
            <Stat value={a.attributed_holders} label="came back" tone="ink" />
            <Stat value={a.attributed_redemptions} label="rewards claimed" tone="ink" />
          </div>
        </div>

        {/* chevron */}
        <span className={`text-[#3a3d44]/40 shrink-0 transition-transform ${openDetail ? "rotate-180" : ""}`}><ChevronDown size={18} /></span>
      </button>

      {openDetail && (
        <div className="border-t border-[#E8E5DE] p-6 bg-[#F8F6F0]/60 rounded-b-2xl" data-testid={`campaign-detail-${c.id}`}>
          {!detail ? (
            <div className="text-[13px] text-[#3a3d44]/55 py-6 text-center">Loading detail…</div>
          ) : (
            <>
              {d && <DeliveryAudit d={d} id={c.id} />}
              <div className="grid md:grid-cols-[1fr_1.4fr] gap-8">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#3a3d44]/50 mb-1">Attribution window</div>
                  <div className="font-display font-bold text-[17px] text-[#0B0D10]">{a.window_days}-day return</div>
                  <p className="text-[13px] text-[#3a3d44]/70 mt-2 leading-relaxed">Of the {a.delivered} pass holders you reached, <span className="text-[#0B0D10] font-semibold">{a.attributed_holders}</span> came back in for a scan within the first {a.window_days} days.
                  {stillMeasuring && <span className="block mt-1 text-[#3a3d44]/50 italic">Still measuring. Window closes {new Date(Date.parse(c.created_at) + a.window_days*86400000).toLocaleDateString(undefined, { day: "numeric", month: "short" })}.</span>}
                  </p>
                  {a.has_snapshot === false && a.attributed_holders === 0 && !stillMeasuring && (
                    <p className="text-[12px] text-[#3a3d44]/45 mt-3 italic">Older campaigns re-derive their audience. The exact snapshot wasn't stored at send.</p>
                  )}
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#3a3d44]/50 mb-3">Visits per day, post-send</div>
                  <AttrChart chart={detail.chart || []} />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DeliveryPill({ d, id }) {
  if (!d) return null;
  const sending = d.status !== "done";
  const attention = !sending && (d.retrying > 0 || d.failed_holders > 0);
  const text = sending ? `Sending ${d.processed_holders}/${d.audience}` : d.retrying > 0 ? `${d.retrying} retrying` : d.failed_holders > 0 ? `${d.failed_holders} failed` : d.installed === 0 ? "No passes in Wallet yet" : "Pushed";
  return (
    <span data-testid={`campaign-delivery-${id}`} className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] rounded-full px-2 py-0.5 ${sending ? "bg-[#0B0D10]/[0.06] text-[#0B0D10]/70" : attention ? "bg-[#FFEBE0] text-[#C2410C]" : "bg-[#EAF7D9] text-[#3a7d2c]"}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${sending ? "bg-[#0B0D10]/50 animate-pulse" : attention ? "bg-[#FF7A3D]" : "bg-[#5BBF3A]"}`} />{text}
    </span>
  );
}

function DeliveryAudit({ d, id }) {
  const items = [
    ["Audience", d.audience, "pass holders in this segment"],
    ["In Wallet", d.installed, d.not_installed ? `${d.not_installed} never finished adding the pass — nothing to push to` : "every pass is on a phone"],
    ["Pushed", d.pushed_holders, `${d.pushed_devices} phone${d.pushed_devices === 1 ? "" : "s"} woken by Apple`],
    ["Updated on phone", d.confirmed_holders, "re-downloaded the pass after the push"],
    ["Retrying", d.retrying, "Apple didn't accept yet — retried automatically"],
    ["Failed", d.failed_holders, "gave up after 8 attempts"],
  ];
  return (
    <div data-testid={`campaign-audit-${id}`} className="mb-6 rounded-xl bg-white border border-[#E8E5DE] p-4">
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-[#3a3d44]/50 mb-3">Delivery audit</div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {items.map(([label, value, hint]) => (
          <div key={label}>
            <div className="font-display font-extrabold text-[20px] leading-none tabular-nums text-[#0B0D10]">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] mt-1.5 text-[#3a3d44]/50">{label}</div>
            <div className="text-[11px] text-[#3a3d44]/50 mt-1 leading-snug">{hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ value, label, tone, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`font-display font-extrabold text-[22px] leading-none tabular-nums ${tone === "lime" ? "text-[#0B0D10]" : "text-[#0B0D10]"}`}>{value}</div>
      <div className={`text-[10px] uppercase tracking-[0.12em] mt-1.5 ${tone === "lime" ? "text-[#0B0D10]/50" : "text-[#3a3d44]/45"}`}>{label}</div>
    </div>
  );
}

function AttrChart({ chart }) {
  if (!chart.length) return <div className="text-[13px] text-[#3a3d44]/50">No activity yet.</div>;
  const max = Math.max(1, ...chart.map((d) => d.visits));
  return (
    <div>
      <div className="flex items-end gap-2 h-[110px]">
        {chart.map((d, i) => {
          const h = Math.max(4, Math.round(d.visits / max * 100));
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full flex items-end justify-center h-full">
                <div style={{ height: `${h}%`, background: d.visits > 0 ? "#0B0D10" : "#E4E0D6" }} className="w-full rounded-t-[4px] transition-all" title={`${d.visits} visits`} />
              </div>
              <span className="text-[10px] text-[#3a3d44]/55 uppercase tracking-[0.08em]">{d.day}</span>
              <span className="text-[10.5px] font-semibold text-[#0B0D10] tabular-nums">{d.visits}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
